<script setup lang="ts">
/**
 * Left pane of the matter-context modal. Three vertically-stacked
 * sections (matter header, knowledge, files), the progress bar, and
 * the primary "Extract" button. The button is the sole entry-point
 * for triggering extraction (one-shot per modal open) — disabled
 * until knowledge text or at least one file is staged.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMatterContextModalStore } from '../../stores/matterContextModalStore';
import assistantIcon from '../../assets/assistant.svg';
import MatterContextHeader from './MatterContextHeader.vue';
import MatterContextKnowledgeSection from './MatterContextKnowledgeSection.vue';
import MatterContextFilesSection from './MatterContextFilesSection.vue';
import MatterContextProgressBar from './MatterContextProgressBar.vue';
import DiscardChangesDialog from './DiscardChangesDialog.vue';

const { t } = useI18n();
const store = useMatterContextModalStore();

// Explains why "Analyze knowledge and files" is disabled, surfaced as a
// tooltip. Empty once the button is enabled (no tooltip shown then).
const extractionDisabledReason = computed(() => {
    if (store.canRunExtraction) return '';
    if (store.loadingExisting) return t('matterContext.leftPane.disabledLoading');
    if (store.extractionState === 'analyzing') return t('matterContext.leftPane.disabledAnalyzing');
    if (store.hasInvalidFiles) return t('matterContext.leftPane.disabledInvalidFiles');
    return t('matterContext.leftPane.disabledNoInput');
});

// Guards a re-extraction that would discard in-session edits to loaded context
// (re-extraction reloads from the published set — see the store).
const reExtractConfirmVisible = ref(false);

const onExtract = () => {
    if (store.hasUnpublishedReviewEdits) {
        reExtractConfirmVisible.value = true;
        return;
    }
    store.runExtraction();
};

const onReExtractConfirm = () => {
    reExtractConfirmVisible.value = false;
    store.runExtraction();
};

const onReExtractCancel = () => {
    reExtractConfirmVisible.value = false;
};
</script>

<template>
    <div class="mc-left-pane">
        <div class="mc-left-pane-scroll">
            <MatterContextHeader />
            <MatterContextKnowledgeSection />
            <MatterContextFilesSection />
        </div>

        <div class="mc-left-pane-footer">
            <MatterContextProgressBar />
            <span
                v-if="store.extractionState === 'idle' || store.progressDismissed"
                class="mc-extract-btn-wrap"
            >
                <button
                    class="mc-extract-button"
                    type="button"
                    :disabled="!store.canRunExtraction"
                    @click="onExtract"
                >
                    <img :src="assistantIcon" alt="" class="mc-extract-button-icon" />
                    <span>{{ t('matterContext.leftPane.analyzeButton') }}</span>
                    <span
                        v-if="extractionDisabledReason"
                        class="material-icons mc-extract-hint-icon"
                        aria-hidden="true"
                    >help_outline</span>
                </button>
                <span
                    v-if="extractionDisabledReason"
                    class="mc-extract-tooltip"
                    role="tooltip"
                >
                    {{ extractionDisabledReason }}
                </span>
            </span>
            <div v-if="store.errorMessage" class="mc-error">{{ store.errorMessage }}</div>
        </div>

        <DiscardChangesDialog
            :visible="reExtractConfirmVisible"
            :title="t('matterContext.leftPane.reExtractTitle')"
            :body="t('matterContext.leftPane.reExtractBody')"
            :cancel-label="t('matterContext.leftPane.keepEditing')"
            :discard-label="t('matterContext.leftPane.reExtractConfirm')"
            @discard="onReExtractConfirm"
            @cancel="onReExtractCancel"
        />
    </div>
</template>
